import { useEffect, useMemo, useState } from "react";
import {
  DEFAULT_TERMINOLOGY,
  resolveTerminologyMap,
  type TerminologyKey,
  type TerminologyMap,
} from "@ease/shared";
import { apiFetch } from "./api-client";
import { useAuth } from "./auth-provider";

export type { TerminologyMap } from "@ease/shared";

type TerminologyRuntime = TerminologyMap & {
  t: (key: TerminologyKey) => string;
};

export function useTerminology() {
  const { isAuthenticated } = useAuth();
  const [terminology, setTerminology] = useState<TerminologyMap>(DEFAULT_TERMINOLOGY);

  useEffect(() => {
    if (!isAuthenticated) {
      setTerminology(DEFAULT_TERMINOLOGY);
      return;
    }

    let isCancelled = false;
    // Tenant context resolved server-side via tenantResolver middleware (session cookie).
    // No tenant ID in URL — per CLAUDE.md architecture.
    apiFetch<{ data: { value: Partial<TerminologyMap> | null } }>(
      "/platform/config/resolve/crm/terminology",
    )
      .then((response) => {
        if (isCancelled) return;
        setTerminology(resolveTerminologyMap(response.data.value));
      })
      .catch(() => {
        if (isCancelled) return;
        setTerminology(DEFAULT_TERMINOLOGY);
      });

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated]);

  return useMemo<TerminologyRuntime>(() => ({
    ...terminology,
    t: (key) => terminology[key],
  }), [terminology]);
}
